import { Search, Plus, X } from 'lucide-react';
import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { CanAccess } from '@/components/CanAccess';
import { UsersFilters } from './UsersFilters';

interface UserTableHeaderProps {
  searchTerm: string;
  setSearchTerm: (_value: string) => void;
  onNewUser: () => void;
  onImportCsv: () => void;
  type: string;
  roles: string[];
  status: string;
  partnerIds: string[];
  onTypeChange: (_value: string) => void;
  onRolesChange: (_value: string[]) => void;
  onStatusChange: (_value: string) => void;
  onPartnerIdsChange: (_value: string[]) => void;
  onClearFilters: () => void;
}

export const UserTableHeader: React.FC<UserTableHeaderProps> = ({
  searchTerm,
  setSearchTerm,
  onNewUser,
  onImportCsv,
  type,
  roles,
  status,
  partnerIds,
  onTypeChange,
  onRolesChange,
  onStatusChange,
  onPartnerIdsChange,
  onClearFilters,
}) => {
  const hasActiveFilters = Boolean(searchTerm || type || roles.length || status || partnerIds.length);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold leading-tight text-gray-900 lg:text-3xl">
            Gerenciamento de usuários
          </h1>
          <p className="max-w-2xl text-sm text-gray-600 sm:text-base">
            Gerencie e controle usuários e seus níveis de acesso na plataforma.
          </p>
        </div>
        <div className="flex h-auto w-full flex-col items-stretch gap-2 sm:h-9 sm:w-auto sm:flex-row sm:items-center">
          <CanAccess roles={['ADMIN', 'MODERATOR', 'CURATOR', 'SUPPORT']}>
            <Button
              type="button"
              onClick={onNewUser}
              className="h-9 w-full gap-2 rounded-lg bg-[#171717] px-4 py-2 text-sm font-medium leading-5 text-[#FAFAFA] shadow-[0px_1px_2px_rgba(0,0,0,0.05)] hover:bg-[#171717]/90 sm:w-[142px]"
            >
              <Plus className="h-4 w-4 text-[#FAFAFA]" strokeWidth={1.33} />
              Novo usuário
            </Button>
          </CanAccess>
          <CanAccess roles={['ADMIN', 'MODERATOR']}>
            <Button
              type="button"
              onClick={onImportCsv}
              className="h-9 w-full rounded-lg border-0 bg-[#F5F5F5] px-4 py-2 text-sm font-medium leading-5 text-[#171717] shadow-[0px_1px_2px_rgba(0,0,0,0.05)] hover:bg-[#EBEBEB] sm:w-[121px]"
            >
              Importar CSV
            </Button>
          </CanAccess>
        </div>
      </div>
      <div className="flex min-w-0 flex-col gap-2 md:flex-row md:items-center">
        <div className="relative w-full md:max-w-md md:flex-1">
          <Input
            placeholder="Buscar por nome, e-mail ou whatsapp"
            value={searchTerm}
            className="h-10 w-full pr-10"
            iconLeft={<Search className="h-4 w-4 stroke-muted-foreground" />}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm ? (
            <button
              type="button"
              aria-label="Limpar busca"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
              onClick={() => setSearchTerm('')}
            >
              <X className="h-4 w-4" />
            </button>
          ) : null}
        </div>
        <UsersFilters
          type={type}
          roles={roles}
          status={status}
          partnerIds={partnerIds}
          onTypeChange={onTypeChange}
          onRolesChange={onRolesChange}
          onStatusChange={onStatusChange}
          onPartnerIdsChange={onPartnerIdsChange}
          onClear={onClearFilters}
          hasActiveFilters={hasActiveFilters}
        />
      </div>
    </div>
  );
};
